import { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import type { AxiosOptions } from 'shared/api'
import { API_URL_MICROSERVICE } from 'shared/config'
import { baseApi } from 'entities/Auth'
import {
  type IResponseContact,
  type IResponseContactsActivecampaignSearch,
  type IResponseContactsHubspotSearch,
  type IResponseContactsInfusionsoftSearch,
  type IResponseContactsPipedriveSearch,
  type IResponseContactsSalesforceSearch,
  type IResponseGetIntegrationsUnifiedContacts,
} from 'entities/Contacts/api/types'
import type { Integration } from 'entities/Integrations/model/Integration'
import { MICROSERVICE_INTEGRATIONS } from 'entities/Integrations/lib/constants'
import {
  type IConnectTwilioParams,
  type ICrmUser,
  type ICrmUserIntegrationMeta,
  type IGetIntegrationVersionResponse,
  type IHubspotPropertiesObjectType,
  type IIntegrationKey,
  type IIntegrationLinkDTO,
  type IIntegrationOrganizationSetting,
  type IIntegrationOrganizationSettingsResponse,
  type IIntegrationSearchMatchingContactsParams,
  type IIntegrationsListResponse,
  IntegrationKey,
  type IParamsAddMessengerMapping,
  type IParamsAutomationLogsList,
  type IParamsCalendlyIntegrationMeetings,
  type IParamsConnectActiveCampaign,
  type IParamsCreateHubspotExtensionInvitationRequest,
  type IParamsGetHubspotExtensionUserDetect,
  type IParamsGetTwilioAccount,
  type IParamsIntegrationActivecampaignTagsApply,
  type IParamsIntegrationContact,
  type IParamsIntegrationInfusionsoftTagsApply,
  type IParamsIntegrations,
  type IParamsIntegrationsByKeyFields,
  type IParamsIntegrationsByKeyFieldsOptions,
  type IParamsIntegrationsContactsSearch,
  type IParamsPagination,
  type IParamsPatchIntegrationUserFieldValue,
  type IParamsPutCalendlyIntegrationUserFieldValue,
  type IParamsSaveHubspotCallTypeByCallSid,
  type IParamsSaveHubspotOutcomeByCallSid,
  type IParamsSearchAssociatedIntegrationContact,
  type IParamsSetHubspotContactsLists,
  type IParamsSettingDict_1_0,
  type IResponseAutoMapIntegrationUsers,
  type IResponseAutomationLogs,
  type IResponseAutomationLogsFilters,
  type IResponseCalendlyIntegrationState,
  type IResponseCheckIntegrationConnectById,
  type IResponseConnectTwilio,
  type IResponseCreateHubspotExtensionInvitationRequest,
  type IResponseGetCalendlyIntegrationMeetings,
  type IResponseGetIntegrationMeetingsFields,
  type IResponseGetConnectData,
  type IResponseGetHubspotActivityTypes,
  type IResponseGetHubspotContactsList,
  type IResponseGetHubspotExtensionUserDetect,
  type IResponseGetHubspotOutcome,
  type IResponseGetIntegrationChannels,
  type IResponseGetIntegrationMeetings,
  type IResponseGetIntegrationObjects,
  type IResponseGetIntegrationObjectsMetadata,
  type IResponseGetIntegrationSettings,
  type IResponseGetIntegrationUsers,
  type IResponseGetTwilioAccount,
  type IResponseHubspotList,
  type IResponseInfusionsoftInfo,
  type IResponseInfusionsoftTags,
  type IResponseIntegration,
  type IResponseIntegrationActivecampaignTagsApply,
  type IResponseIntegrationCategory,
  type IResponseIntegrationContact,
  type IResponseIntegrationContactsMatchingSearch,
  type IResponseIntegrationHubspotCompany,
  type IResponseIntegrationHubspotDeal,
  type IResponseIntegrationHubspotProperties,
  type IResponseIntegrationHubspotTicket,
  type IResponseIntegrationInfusionsoftTagsApply,
  type IResponseIntegrations,
  type IResponseIntegrationsContactsActiveCampaignInfo,
  type IResponseIntegrationsContactsHubspotInfo,
  type IResponseIntegrationsContactsPipedriveInfo,
  type IResponseIntegrationsContactsSalesforceInfo,
  type IResponseMergeField,
  type IResponseMessengerMapping,
  type IResponseSaveHubspotCallTypeByCallSid,
  type IResponseSaveHubspotOutcomeByCallSid,
  type IResponseSearchAssociatedIntegrationContact,
  type IResponseSettingDict_1_0,
  type IResponseSlackIntegrationChannels,
  type IResponseSlackMapping,
  type IResponseUserIntegration,
  type ISalesforcePropertiesObjectType,
  type ISettingRequestBySettingDict,
  type ISettingRequestOptionsBySettingDict,
} from 'entities/Integrations/api/types'
import { type IAmpersandSettings } from 'entities/Ampersand'

class Api {
  getIntegrations(
    params?: IParamsIntegrations,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrations>> {
    return baseApi.get('integrations/list', { params, ...options })
  }

  getIntegrationsList(): Promise<AxiosResponse<IIntegrationsListResponse>> {
    return baseApi.get('integrations')
  }

  getIntegrationByKey(key: IIntegrationKey): Promise<AxiosResponse<IResponseIntegration>> {
    return baseApi.get(`integrations/${key}`)
  }

  getIntegrationsCategory(): Promise<AxiosResponse<IResponseIntegrationCategory>> {
    return baseApi.get('/integrations/category')
  }

  checkIntegrationConnectById(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<IResponseCheckIntegrationConnectById>> {
    return baseApi.get(`integrations/${integrationKey}/check`)
  }

  getIntegrationsContactsActivecampaign(
    params: IParamsIntegrationsContactsSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseContactsActivecampaignSearch>> {
    return baseApi.get('integrations/contacts/activecampaign', { params, ...options })
  }

  getIntegrationsContactsInfusionsoft(
    params: IParamsIntegrationsContactsSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseContactsInfusionsoftSearch>> {
    return baseApi.get('integrations/contacts/infusionsoft', { params, ...options })
  }

  getIntegrationsContactsPipedrive(
    params: IParamsIntegrationsContactsSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseContactsPipedriveSearch>> {
    return baseApi.get('integrations/contacts/pipedrive', { params, ...options })
  }

  getIntegrationsContactsSalesforce(
    params: IParamsIntegrationsContactsSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseContactsSalesforceSearch>> {
    return baseApi.get('integrations/contacts/salesforce', { params, ...options })
  }

  getIntegrationsContactsHubspot(
    params: IParamsIntegrationsContactsSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseContactsHubspotSearch>> {
    return baseApi.get('integrations/contacts/hubspot', { params, ...options })
  }

  getIntegrationsUnifiedContacts(
    key: IIntegrationKey,
    params: IParamsIntegrationsContactsSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetIntegrationsUnifiedContacts>> {
    return baseApi.get(`integrations/contacts/unified-connection/${key}`, {
      params,
      ...options,
    })
  }

  getIntegrationsContactMergeFields(
    id: number,
    query?: string
  ): Promise<AxiosResponse<IResponseMergeField[]>> {
    return baseApi.get(`integrations/${id}/merge-fields`, { params: { query } })
  }

  getAllFields(): Promise<AxiosResponse<{ [key in IIntegrationKey]: IResponseMergeField[] }>> {
    return baseApi.get('integrations/all-fields')
  }

  getIntegrationsByKeyFields(
    key: IParamsIntegrationsByKeyFields,
    params?: IParamsIntegrationsByKeyFieldsOptions
  ): Promise<AxiosResponse<IResponseMergeField[]>> {
    return baseApi.get(`integrations/${key}/fields`, {
      params,
    })
  }

  getHubspotFields(
    params: { contact_id: string | number; search: string },
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseMergeField[]>> {
    return baseApi.get('integrations/hubspot/fields', {
      ...options,
      params: { ...params, is_ui_setting: 1 },
    })
  }

  getHubspotFieldsByType(
    params: { search: string; objectType: IHubspotPropertiesObjectType; objectId: number },
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseMergeField[]>> {
    const { objectType, objectId, search } = params
    return baseApi.get(`integrations/hubspot/${objectType}/${objectId}/fields`, {
      ...options,
      params: { search },
    })
  }

  getContactsIntegrationsHubspotTicketInfo(
    contactId: number,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrationHubspotTicket[]>> {
    return baseApi.get(`integrations/hubspot/ticket/${contactId}/info`, options)
  }

  getContactsIntegrationsHubspotCompanyInfo(
    contactId: number,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrationHubspotCompany[]>> {
    return baseApi.get(`integrations/hubspot/company/${contactId}/info`, options)
  }

  getContactsIntegrationsHubspotDealInfo(
    contactId: number,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrationHubspotDeal[]>> {
    return baseApi.get(`integrations/hubspot/deal/${contactId}/info`, options)
  }

  getContactsIntegrationsHubspotContactInfo(
    contactId: number,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrationHubspotProperties>> {
    return baseApi.get(`integrations/hubspot/contact/${contactId}/info`, options)
  }

  getContactsIntegrationsHubspotInfo(
    params: {
      id: number
      use_contact_card_properties?: boolean
    },
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrationsContactsHubspotInfo>> {
    const { id, use_contact_card_properties } = params
    return baseApi.get(`hubspot/contact/${id}/info`, {
      ...options,
      params: { use_contact_card_properties },
    })
  }

  getContactsIntegrationsActiveCampaignInfo(
    id: number,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrationsContactsActiveCampaignInfo>> {
    return baseApi.get(`activecampaign/contact/${id}/info`, options)
  }

  getContactsIntegrationsPipedriveInfo(
    id: number,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrationsContactsPipedriveInfo>> {
    return baseApi.get(`pipedrive/extension/contact/${id}/info`, options)
  }

  getContactsIntegrationsSalesforceInfo(
    id: number,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrationsContactsSalesforceInfo>> {
    return baseApi.get(`salesforce/contact/${id}/info`, options)
  }

  getSalesforceFieldsByType(
    params: { objectType: ISalesforcePropertiesObjectType },
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseMergeField[]>> {
    const { objectType } = params
    return baseApi.get(`/integrations/salesforce/${objectType}/fields`, {
      ...options,
    })
  }

  getContactsIntegrationsInfusionsoftInfo(
    id: number,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseInfusionsoftInfo>> {
    return baseApi.get(`infusionsoft/contact/${id}/info`, options)
  }

  unlinkContactsByIdIntegrations(id: number): Promise<AxiosResponse<IResponseContact>> {
    return baseApi.put(`integrations/contacts/${id}/unlink`)
  }

  createContactsIntegrationsActiveCampaignTag(
    data: IParamsIntegrationActivecampaignTagsApply
  ): Promise<AxiosResponse<IResponseIntegrationActivecampaignTagsApply>> {
    return baseApi.put('/integrations/activecampaign/tags/apply', data)
  }

  createContactsIntegrationsInfusionsoftTag(
    data: IParamsIntegrationInfusionsoftTagsApply
  ): Promise<AxiosResponse<IResponseIntegrationInfusionsoftTagsApply>> {
    return baseApi.put('/integrations/infusionsoft/tags/apply', data)
  }

  getContactsIntegrationsInfusionsoftTags(
    value: string
  ): Promise<AxiosResponse<IResponseInfusionsoftTags>> {
    return baseApi.get(`integrations/infusionsoft/tags?query=${value}`)
  }

  linkContactsIntegrations(data: IIntegrationLinkDTO): Promise<AxiosResponse<IResponseContact>> {
    return baseApi.post(`contacts/${data.contact_id}/link`, data)
  }

  searchMatchingContactsIntegrations(
    params: IIntegrationSearchMatchingContactsParams
  ): Promise<AxiosResponse<IResponseIntegrationContactsMatchingSearch>> {
    return baseApi.get('/integrations/contacts/search', { params })
  }

  connectTwilio(params: IConnectTwilioParams): Promise<AxiosResponse<IResponseConnectTwilio>> {
    return baseApi.post('integrations/number-vendors/twilio-integration/connect', params)
  }

  getHubspotLists(): Promise<AxiosResponse<IResponseHubspotList[]>> {
    return baseApi.get('hubspot/lists/contact/processed')
  }

  getHubspotContactsLists(): Promise<AxiosResponse<IResponseGetHubspotContactsList[]>> {
    return baseApi.get('hubspot/lists/contact')
  }

  resyncHubspotLists(listId: number): Promise<AxiosResponse<IResponseHubspotList>> {
    return baseApi.post(`hubspot/lists/resync/${listId}`)
  }

  setHubspotContactsLists(
    params: IParamsSetHubspotContactsLists
  ): Promise<AxiosResponse<IResponseConnectTwilio>> {
    return baseApi.post('hubspot/lists/process', params)
  }

  getIntegrationContact(
    params: IParamsIntegrationContact
  ): Promise<AxiosResponse<IResponseIntegrationContact>> {
    return baseApi.get('extension/contact', {
      params,
    })
  }

  linkIntegrationContact(
    id: number,
    params: IParamsIntegrationContact
  ): Promise<AxiosResponse<IResponseContact>> {
    return baseApi.post(`extension/contact/${id}/link`, params)
  }
  searchAssociatedIntegrationContact(
    integrationKey: IIntegrationKey,
    params: IParamsSearchAssociatedIntegrationContact,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseSearchAssociatedIntegrationContact>> {
    return baseApi.get(`integrations/contacts/${integrationKey}`, { params, ...options })
  }
  getHubspotExtensionUserDetect(
    params: IParamsGetHubspotExtensionUserDetect
  ): Promise<AxiosResponse<IResponseGetHubspotExtensionUserDetect>> {
    return baseApi.get('hubspot/extension/user/detect', {
      params,
    })
  }
  createHubspotExtensionInvitationRequest(
    params: IParamsCreateHubspotExtensionInvitationRequest
  ): Promise<AxiosResponse<IResponseCreateHubspotExtensionInvitationRequest>> {
    return baseApi.post('hubspot/extension/user/invitation/request', params)
  }

  getIntegrationMeetings(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<IResponseGetIntegrationMeetings>> {
    return baseApi.get(`integrations/${integrationKey}/meeting`)
  }

  getCalendlyIntegrationMeetings(
    integrationKey: IIntegrationKey,
    params: IParamsCalendlyIntegrationMeetings
  ): Promise<AxiosResponse<IResponseGetCalendlyIntegrationMeetings>> {
    return baseApi.get(`calendar/api/v1/event-types/${integrationKey}`, { params })
  }

  getCalendlyIntegrationMeetingsFields(
    integrationKey: IIntegrationKey,
    params: { id: string }
  ): Promise<AxiosResponse<IResponseGetIntegrationMeetingsFields>> {
    return baseApi.get(`calendar/api/v1/event-types/${integrationKey}/show/${params.id}`)
  }

  getHubspotIntegrationMeetingsFields(
    integrationKey: IIntegrationKey,
    params: { id: string }
  ): Promise<AxiosResponse<IResponseGetIntegrationMeetingsFields>> {
    return baseApi.get(
      `calendar/api/v1/event-types/${integrationKey}/show-by-slug?slug=${params.id}`
    )
  }

  getCalendlyIntegrationState(): Promise<AxiosResponse<IResponseCalendlyIntegrationState>> {
    return baseApi.get('calendar/api/v1/auth/state/calendly')
  }

  getConnectIntegrationRedirectUrl(integrationKey: IIntegrationKey): string {
    if (MICROSERVICE_INTEGRATIONS.includes(integrationKey)) {
      return `${API_URL_MICROSERVICE}integration/connect/${integrationKey}`
    }

    if (integrationKey === IntegrationKey.calendly) {
      return `${API_URL_MICROSERVICE}calendar/api/v1/auth/connect/${integrationKey}`
    }

    return `${API_URL_MICROSERVICE}core/integrations/${integrationKey}/connect`
  }
  getConnectData(integrationKey: IIntegrationKey): Promise<AxiosResponse<IResponseGetConnectData>> {
    return baseApi.get(`integration/connect/${integrationKey}`)
  }
  getReconnectData(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<IResponseGetConnectData>> {
    return baseApi.get(`integration/reconnect/${integrationKey}`)
  }

  getReconnectIntegrationRedirectUrl(integration: Integration, isSandbox = false): string {
    const integrationKey = integration.key
    const userIntegrationId = integration.connected?.id
    const queryParams = userIntegrationId
      ? `?${new URLSearchParams({ id: userIntegrationId.toString() })}`
      : ''

    if (MICROSERVICE_INTEGRATIONS.includes(integrationKey) || integration.isNangoProvider) {
      const sandboxQuery = isSandbox ? '?is_sandbox=true' : ''
      return `${API_URL_MICROSERVICE}integration/reconnect/${integrationKey}${sandboxQuery}`
    }

    if (integrationKey === IntegrationKey.calendly) {
      return `${API_URL_MICROSERVICE}calendar/api/v1/auth/reconnect/${integrationKey}`
    }

    return `${API_URL_MICROSERVICE}core/integrations/${integrationKey}/reconnect${queryParams}`
  }

  connectActiveCampaign({
    integrationKey,
    apiUrl,
    apiKey,
  }: IParamsConnectActiveCampaign): Promise<AxiosResponse<IResponseUserIntegration>> {
    return baseApi.post(`integrations/${integrationKey}/connect`, {
      url: apiUrl,
      api_key: apiKey,
    })
  }

  disconnectIntegration(integration: Integration) {
    const integrationKey = integration.key
    const userIntegrationId = integration.connected?.id
    const params = userIntegrationId ? { id: userIntegrationId } : null

    if (integration.isNangoProvider && integration.connected?.provider_user_id) {
      return this.deleteInstallation(integrationKey, integration.connected?.provider_user_id)
    }

    if (MICROSERVICE_INTEGRATIONS.includes(integrationKey)) {
      return baseApi.put(`integration/disconnect/${integrationKey}`, params)
    }

    if (integrationKey === IntegrationKey.calendly) {
      return baseApi.put(`calendar/api/v1/auth/disconnect/${integrationKey}`)
    }

    if (integration.is_ampersand) {
      return baseApi.delete(`integration/installations/${integration.key}/delete`)
    }

    return baseApi.put(`integrations/${integrationKey}/disconnect`, params)
  }
  getIntegrationSettings<T>(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<IResponseGetIntegrationSettings<T>>> {
    return baseApi.get(`integration/settings/${integrationKey}`)
  }
  putIntegrationSettings<T>(
    integrationKey: IIntegrationKey,
    data: ISettingRequestBySettingDict<T>,
    config: AxiosRequestConfig<ISettingRequestBySettingDict<T>>
  ): Promise<AxiosResponse<IResponseGetIntegrationSettings<T>>> {
    return baseApi.put(`integration/settings/${integrationKey}`, data, config)
  }
  putIntegrationSettings1_0(
    data: IParamsSettingDict_1_0,
    config: AxiosRequestConfig<IParamsSettingDict_1_0>
  ): Promise<AxiosResponse<IResponseSettingDict_1_0>> {
    return baseApi.patch('organization/settings', data, config)
  }
  getIntegrationUsersList(
    integrationKey: IIntegrationKey,
    params: IParamsPagination,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetIntegrationUsers>> {
    return baseApi.get(`integrations/${integrationKey}/users`, {
      params,
      ...options,
    })
  }
  getCalendlyIntegrationUsersList(
    integrationKey: IIntegrationKey,
    params: IParamsPagination,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetIntegrationUsers>> {
    return baseApi.get(`calendar/api/v1/user-mapping/${integrationKey}`, {
      params,
      ...options,
    })
  }
  patchIntegrationUserFieldValue(
    integrationKey: IIntegrationKey,
    userId: number,
    data: IParamsPatchIntegrationUserFieldValue
  ): Promise<AxiosResponse<ICrmUser<ICrmUserIntegrationMeta>>> {
    return baseApi.patch(`integrations/${integrationKey}/users/${userId}`, data)
  }
  putCalendlyIntegrationUserFieldValue(
    integrationKey: IIntegrationKey,
    userId: number,
    data: IParamsPutCalendlyIntegrationUserFieldValue
  ): Promise<AxiosResponse<ICrmUser<ICrmUserIntegrationMeta>>> {
    return baseApi.put(`calendar/api/v1/user-mapping/${integrationKey}/update/${userId}`, data)
  }
  getIntegrationSettingsList<T>(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<ISettingRequestOptionsBySettingDict<T>>> {
    return baseApi.get(`integration/settings/${integrationKey}/list`)
  }
  getIntegrationChannels(
    integrationKey: IIntegrationKey,
    params: IParamsPagination,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetIntegrationChannels>> {
    return baseApi.get(`integrations/${integrationKey}/channels`, {
      params,
      ...options,
    })
  }
  activateIntegrationRefresh(integrationKey: IIntegrationKey): Promise<AxiosResponse> {
    return baseApi.post('integrations/refresh', {
      integration: integrationKey,
    })
  }
  activateCalendlyIntegrationRefresh(integrationKey: IIntegrationKey): Promise<AxiosResponse> {
    return baseApi.post(`calendar/api/v1/user-mapping/${integrationKey}/refresh`)
  }

  activateCalendlyIntegrationAutoMappingUsers(): Promise<
    AxiosResponse<IResponseAutoMapIntegrationUsers>
  > {
    return baseApi.post('calendar/api/v1/user-mapping/calendly/auto-mapping-users')
  }

  getTwilioAccount(
    params: IParamsGetTwilioAccount
  ): Promise<AxiosResponse<IResponseGetTwilioAccount>> {
    return baseApi.get('addons/twilio', {
      params,
    })
  }
  getIntegrationOrganizationSetting(
    options?: AxiosOptions
  ): Promise<AxiosResponse<IIntegrationOrganizationSettingsResponse>> {
    return baseApi.get('integration/organization-settings', options)
  }

  putIntegrationOrganizationSetting(
    data: IIntegrationOrganizationSetting
  ): Promise<AxiosResponse<IIntegrationOrganizationSettingsResponse>> {
    return baseApi.put('integration/organization-settings', data)
  }

  getIntegrationVersion(
    integrationKey: IIntegrationKey,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IGetIntegrationVersionResponse>> {
    return baseApi.get(`integrations/${integrationKey}/versions/active`, options)
  }

  getHubspotActivityTypes(
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetHubspotActivityTypes>> {
    return baseApi.get('integrations/hubspot/activity-types', {
      ...options,
    })
  }

  saveHubspotCallTypeByCallSid(
    params: IParamsSaveHubspotCallTypeByCallSid
  ): Promise<AxiosResponse<IResponseSaveHubspotCallTypeByCallSid>> {
    return baseApi.post('integrations/hubspot/call-type', params)
  }

  getHubspotOutcome(options?: AxiosOptions): Promise<AxiosResponse<IResponseGetHubspotOutcome>> {
    return baseApi.get('integrations/hubspot/outcome', {
      ...options,
    })
  }
  saveHubspotOutcomeByCallSid(
    params: IParamsSaveHubspotOutcomeByCallSid
  ): Promise<AxiosResponse<IResponseSaveHubspotOutcomeByCallSid>> {
    return baseApi.post('integrations/hubspot/outcome', params)
  }

  getAirCallIntegrationLink(): Promise<AxiosResponse<string>> {
    return baseApi.get('core/aircall/integration/link')
  }

  getAutomationLogs(
    params: IParamsAutomationLogsList,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseAutomationLogs>> {
    return baseApi.post('integrations/automation-log-steps/list', params, options)
  }

  getAutomationLogsFilters(): Promise<AxiosResponse<IResponseAutomationLogsFilters>> {
    return baseApi.get('integrations/automation-log-steps/filters')
  }
  getAmpersandAppSettings(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<IAmpersandSettings>> {
    return baseApi.get(`integration/settings/${integrationKey}/app`)
  }
  createInstallation(
    integration_key: IIntegrationKey,
    installationId: string
  ): Promise<AxiosResponse<void>> {
    return baseApi.post(`integration/installations/${integration_key}/created/${installationId}`)
  }
  updateInstallation(
    integration_key: IIntegrationKey,
    installationId: string
  ): Promise<AxiosResponse<IAmpersandSettings>> {
    return baseApi.put(`integration/installations/${integration_key}/updated/${installationId}`)
  }
  deleteInstallation(
    integration_key: IIntegrationKey,
    installationId: string
  ): Promise<AxiosResponse<IAmpersandSettings>> {
    return baseApi.delete(`integration/installations/${integration_key}/deleted/${installationId}`)
  }
  autoMapIntegrationUsers(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<IResponseAutoMapIntegrationUsers>> {
    return baseApi.post(`integrations/${integrationKey}/auto-mapping-users`)
  }
  getSlackIntegrationChannels(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<IResponseSlackIntegrationChannels>> {
    return baseApi.get('core/messenger/channels', {
      params: { integration: integrationKey },
    })
  }
  getMapInboxesToIntegrationChannels(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<IResponseSlackMapping>> {
    return baseApi.get('core/messenger/mapping', {
      params: { integration: integrationKey },
    })
  }
  addMessengerMapping(
    data: IParamsAddMessengerMapping
  ): Promise<AxiosResponse<IResponseMessengerMapping>> {
    return baseApi.post('core/messenger/mapping/add', data)
  }
  removeSlackMapping(integrationKey: IIntegrationKey, channelId: string): Promise<AxiosResponse> {
    return baseApi.delete('core/messenger/mapping/delete', {
      params: { integration: integrationKey, channel_id: channelId },
    })
  }
  getIntegrationObjects(
    integrationKey: IIntegrationKey
  ): Promise<AxiosResponse<IResponseGetIntegrationObjects>> {
    return baseApi.get(`integration/objects/${integrationKey}`)
  }
  getIntegrationObjectsMetadata(
    integrationKey: IIntegrationKey,
    objectName: string
  ): Promise<AxiosResponse<IResponseGetIntegrationObjectsMetadata>> {
    return baseApi.get(`integration/objects/${integrationKey}/write-metadata/${objectName}`)
  }
}

export const IntegrationsApi = new Api()
